import type { TransitionType } from "@/timeline";

/**
 * Transition registry (B4).
 *
 * One definition per transition type. `resolve` maps the seam progress (0..1
 * across the transition window) + which seam node ("incoming" = the right clip's
 * head, "outgoing" = the left clip's tail) to a per-side visual override the
 * renderer applies. Keeping per-type logic here (not in the resolve hot-path)
 * is the clean path to a 6-8 type library.
 */

export type SeamRole = "incoming" | "outgoing";

export interface SeamVisual {
	// Opacity multiplier for this seam side at this progress.
	opacity: number;
}

export interface TransitionDefinition {
	type: TransitionType;
	label: string;
	keywords: string[];
	defaultDurationSeconds: number;
	resolve(params: { progress01: number; role: SeamRole }): SeamVisual;
}

function clamp01(value: number): number {
	return Math.max(0, Math.min(1, value));
}

const crossfade: TransitionDefinition = {
	type: "crossfade",
	label: "Crossfade",
	keywords: ["crossfade", "dissolve", "fade"],
	defaultDurationSeconds: 0.5,
	resolve({ progress01, role }) {
		// Corrected recipe: the frame clears to opaque black and the compositor
		// stacks source-over, so ramping BOTH sides darkens the midpoint. Hold the
		// outgoing side fully opaque on the bottom and ramp only the incoming on
		// top — what CapCut's default cross-dissolve approximates.
		if (role === "incoming") {
			return { opacity: clamp01(progress01) };
		}
		return { opacity: 1 };
	},
};

const registry: Record<TransitionType, TransitionDefinition> = {
	crossfade,
};

export function getTransitionDefinition(
	type: TransitionType,
): TransitionDefinition {
	return registry[type];
}

export function getAllTransitionDefinitions(): TransitionDefinition[] {
	return Object.values(registry);
}
